import { Form, Input, Radio, Switch } from "antd";
import * as React from "react";
import { ObjectO2 } from "src/pages/ManagementPage/Obj2Tab/Obj2Nodes";
import { ZAttribute } from "src/types/ZAttribute";
import { AttrTypeName } from "src/types/AttrType";
import { ZUid } from "src/types/zUid";
import {
  AttrDef,
  BsAttrSection,
} from "src/businessServices/BsObjectSection/BsAttrSection";
import { loadAttributeInfo } from "src/pages/ManagementPage/objectsApi";
import { onError } from "src/common/onError";
import styles from "./Planning2Settings.module.less";
import { BsObjectSection } from "../../../BsObjectSection";
import { ZPlanning2Settings } from "../ZPlanning2Settings";

type PropsPlanning2Settings = {
  typesMap: Record<number, string>;
  objList: ObjectO2[];
  mainObjectId: ZUid;
};

const f = (key: keyof ZPlanning2Settings): string => key;

export const Planning2Settings: React.FC<PropsPlanning2Settings> = (props) => {
  const { objList, typesMap, mainObjectId } = props;
  const singleType = (typeName: AttrTypeName) => (attr: ZAttribute) =>
    typesMap[attr.valueType] === typeName;
  const idValue = Form.useWatch(f("id"));
  const curObject =
    idValue === undefined
      ? undefined
      : objList.find(
          (it) => it.type === "obj" && it.object.id === mainObjectId,
        );
  const nestedPlanning = Form.useWatch(f("nestedPlanning"));

  return (
    <>
      <Form.Item name={f("id")} noStyle>
        <Input type="hidden" />
      </Form.Item>
      <h3>{curObject?.object.name}</h3>
      <div style={{ width: "50%" }}>
        <Form.Item
          name={f("planDateReversedCalculation")}
          label="Направление расчета плановых дат"
        >
          <Radio.Group
            defaultValue={false}
            className={styles.radioGroup}
            disabled={!!idValue}
          >
            <Radio value={false}>Вперёд — от даты начала плана</Radio>
            <Radio value>Назад — от даты окончания плана</Radio>
          </Radio.Group>
        </Form.Item>
        <BsAttrSection
          attrDef={{
            label: "Дата начала/окончания календарного плана",
            attrName: f("relationPlanDateAttributeId"),
            attrValid: singleType(AttrTypeName.date),
            optional: true,
          }}
          curObject={curObject}
        />
      </div>

      <h3>Задача</h3>
      <BsObjectSection
        objName={f("taskObjectId")}
        objLabel="Объект Задача"
        objList={objList}
        attrs={[
          {
            label: "Связь с объектом бизнес-сервиса",
            attrName: f("relationLinkAttributeId"),
            attrValid: singleType(AttrTypeName.object),
          },
          {
            label: "Тип задачи",
            attrName: f("taskTypeAttributeId"),
            attrValid: singleType(AttrTypeName.dictSingle),
          },
          {
            label: "Длительность",
            attrName: f("durationAttributeId"),
            attrValid: singleType(AttrTypeName.int),
          },
          {
            label: "Оценка длительности",
            attrName: f("durationEstimationAttributeId"),
            attrValid: singleType(AttrTypeName.int),
          },
          {
            label: "Департамент",
            attrName: f("departmentAttributeId"),
            attrValid: singleType(AttrTypeName.dictSingle),
          },
          {
            label: "Исполнитель",
            attrName: f("employeeAttributeId"),
            attrValid: singleType(AttrTypeName.usersList),
          },
          {
            label: "Приоритет",
            attrName: f("priorityAttributeId"),
            attrValid: singleType(AttrTypeName.dictSingle),
          },
          {
            label: "Группа задач",
            attrName: f("taskGroupAttributeId"),
            attrValid: singleType(AttrTypeName.dictSingle),
          },
          {
            label: "Предыдущая задача",
            attrName: f("previousTaskLinkAttributeId"),
            attrValid: singleType(AttrTypeName.object),
          },
          {
            label: "Название",
            attrName: f("nameAttributeId"),
            attrValid: singleType(AttrTypeName.string),
          },
          {
            label: "Предыдущая задача шаблона",
            attrName: f("previousTemplateTaskLinkAttributeId"),
            attrValid: singleType(AttrTypeName.object),
          },
          {
            label: "Плановая дата начала",
            attrName: f("plannedStartDateAttributeId"),
            attrValid: singleType(AttrTypeName.date),
            onNewLine: true,
          },
          {
            label: "Плановая дата окончания",
            attrName: f("plannedEndDateAttributeId"),
            attrValid: singleType(AttrTypeName.date),
          },
          {
            label: "Фактическая дата начала",
            attrName: f("actualStartDateAttributeId"),
            attrValid: singleType(AttrTypeName.date),
          },
          {
            label: "Фактическая дата окончания",
            attrName: f("actualEndDateAttributeId"),
            attrValid: singleType(AttrTypeName.date),
          },
          {
            label: "Прогнозная дата начала",
            attrName: f("forecastStartDateAttributeId"),
            attrValid: singleType(AttrTypeName.date),
          },
          {
            label: "Прогнозная дата окончания",
            attrName: f("forecastEndDateAttributeId"),
            attrValid: singleType(AttrTypeName.date),
          },
          {
            label: "Отклонение",
            attrName: f("delayAttributeId"),
            attrValid: singleType(AttrTypeName.int),
          },
          {
            label: "Связь Задачи и Шаблона",
            attrName: f("templateLinkAttributeId"),
            attrValid: singleType(AttrTypeName.object),
          },
          {
            label: "Смещение",
            attrName: f("shiftAttributeId"),
            attrValid: singleType(AttrTypeName.int),
          },
          {
            label: "Предыдущая задача другого шаблона",
            attrName: f("previousOtherTemplateTaskLinkAttributeId"),
            attrValid: singleType(AttrTypeName.object),
            optional: true,
          },
          {
            label: "Исходная задача шаблона",
            attrName: f("originTemplateTaskLinkAttributeId"),
            attrValid: singleType(AttrTypeName.object),
            optional: true,
          },
        ]}
      />

      <h3>Вложенное планирование</h3>
      <div className={styles.twoColumns}>
        <div>
          <label className={styles.boolBox}>
            <Form.Item name={f("nestedPlanning")} noStyle>
              <Switch />
            </Form.Item>
            <div>Вложенное планирование</div>
          </label>
        </div>
        <div>
          {nestedPlanning && (
            <label className={styles.boolBox}>
              <Form.Item name={f("checkCompletenessPlan")} noStyle>
                <Switch />
              </Form.Item>
              <div>Проверка полноты плана</div>
            </label>
          )}
        </div>
        {nestedPlanning && (
          <>
            <IndirectAttrSelect
              srcField="relationLinkAttributeId"
              dstField="componentChildAttributeId"
              label="Связь с компонентом"
              objList={objList}
              attrValid={singleType(AttrTypeName.childEntities)}
            />

            <IndirectAttrSelect
              label="Тип компонента"
              srcField="componentChildAttributeId"
              dstField="componentTypeAttributeId"
              objList={objList}
              attrValid={singleType(AttrTypeName.dictSingle)}
            />

            <IndirectAttrSelect
              label="Тип шаблона для привязки"
              srcField="templateLinkAttributeId"
              dstField="templateConnectTypeAttributeId"
              objList={objList}
              attrValid={singleType(AttrTypeName.dictSingle)}
            />

            <AttrSelectForObject
              attr={{
                label: "Ссылка на компонент",
                attrValid: singleType(AttrTypeName.object),
                attrName: f("componentLinkAttributeId"),
              }}
              objField="taskObjectId"
              objList={objList}
            />
          </>
        )}
      </div>

      <div className={styles.footer} />
    </>
  );
};

type PropsAttrSelectForObject = {
  objField: keyof ZPlanning2Settings;
  objList: ObjectO2[];
  attr: AttrDef;
};
const AttrSelectForObject: React.FC<PropsAttrSelectForObject> = (props) => {
  const { attr, objField, objList } = props;
  const objId = Form.useWatch(objField);
  const curObject = findObject(objId, objList);
  return <BsAttrSection curObject={curObject} attrDef={attr} />;
};

/**
 * Выбор атрибута из объекта, который привязан к другому атрибуту через referenceId.
 * То есть, srcField - это поле для выбора атрибута типа childEntities или object, которое находится где-то на форме.
 * dstField - это уже поле, которое контролируется данным компонентом.
 */
type PropsIndirectAttrSelect = Pick<AttrDef, "attrValid" | "label"> & {
  srcField: keyof ZPlanning2Settings;
  dstField: keyof ZPlanning2Settings;
  objList: ObjectO2[];
};
const IndirectAttrSelect: React.FC<PropsIndirectAttrSelect> = (props) => {
  const { srcField, dstField, label, attrValid, objList } = props;
  const srcAttrId = Form.useWatch(srcField);
  const [dstObject, setDstObject] = React.useState<ObjectO2 | undefined>();
  const [loading, setLoading] = React.useState(false);
  const form = Form.useFormInstance();
  React.useEffect(() => {
    form.setFieldValue(dstField, null);
    setLoading(true);
    loadIndirectInfo(srcAttrId, objList)
      .then((obj) => setDstObject(obj))
      .finally(() => setLoading(false))
      .catch(onError);
  }, [srcAttrId]);
  return (
    <BsAttrSection
      curObject={dstObject}
      attrDef={{
        attrName: dstField,
        label,
        attrValid,
      }}
      loading={loading}
    />
  );
};

const loadIndirectInfo = async (
  srcAttrId: string | undefined,
  objList: ObjectO2[],
): Promise<ObjectO2 | undefined> => {
  if (!srcAttrId) return undefined;
  const { referenceId } = await loadAttributeInfo(srcAttrId);
  return findObject(referenceId, objList);
};

const findObject = (
  objectId: ZUid | null | undefined,
  objList: ObjectO2[],
): ObjectO2 | undefined =>
  objectId
    ? objList.find((it) => it.type === "obj" && it.object.id === objectId)
    : undefined;
